/**
 * Lecture des réponses à voix haute (`POST /runtime/voix`, core-ai D45).
 *
 * L'API renvoie du PCM brut en flux : flottants 32 bits petit-boutistes, mono,
 * 24 kHz. On le joue au fil de l'arrivée avec Web Audio, morceau après morceau,
 * sans attendre la fin — c'est ce qui fait entendre la voix en une seconde.
 *
 * Aucune dépendance : `fetch`, `ReadableStream` et `AudioContext` suffisent.
 */

export const FREQUENCE_VOIX = 24_000;

/** Avance prise sur l'horloge audio au premier morceau, pour ne pas craquer. */
const AVANCE_S = 0.05;

/**
 * Transforme des octets reçus en échantillons, en gardant le reste.
 *
 * Un paquet réseau ne tombe pas sur une frontière d'échantillon (4 octets) : les
 * octets en trop attendent le paquet suivant. Les lire tels quels décalerait
 * tout le flux d'un ou deux octets — un bruit blanc assourdissant.
 *
 * `DataView` plutôt qu'un `Float32Array` posé sur le tampon : l'ordre des octets
 * est fixé par le format, pas par la machine, et l'alignement n'est pas garanti.
 */
export function decouperEchantillons(
  reste: Uint8Array,
  morceau: Uint8Array
): { echantillons: Float32Array; reste: Uint8Array } {
  const octets = new Uint8Array(reste.length + morceau.length);
  octets.set(reste, 0);
  octets.set(morceau, reste.length);

  const nombre = Math.floor(octets.length / 4);
  const vue = new DataView(octets.buffer, octets.byteOffset, nombre * 4);
  const echantillons = new Float32Array(nombre);
  for (let i = 0; i < nombre; i++) echantillons[i] = vue.getFloat32(i * 4, true);

  return { echantillons, reste: octets.slice(nombre * 4) };
}

/**
 * PCM 16 bits petit-boutiste → flottants. C'est le format de la voix qui arrive
 * dans le flux du chat (lot 1) : deux fois plus léger que les flottants.
 */
export function pcm16VersFlottants(octets: Uint8Array): Float32Array {
  const nombre = Math.floor(octets.length / 2);
  const vue = new DataView(octets.buffer, octets.byteOffset, nombre * 2);
  const echantillons = new Float32Array(nombre);
  for (let i = 0; i < nombre; i++) echantillons[i] = vue.getInt16(i * 2, true) / 32768;
  return echantillons;
}

function base64VersOctets(base64: string): Uint8Array {
  const brut = atob(base64);
  const octets = new Uint8Array(brut.length);
  for (let i = 0; i < brut.length; i++) octets[i] = brut.charCodeAt(i);
  return octets;
}

type ContexteAudio = AudioContext;

/** Une lecture en cours : où placer le morceau suivant sur l'horloge audio. */
type Programmation = {
  lecture: AbortController;
  prochain: number;
  derniere: AudioBufferSourceNode | null;
  terminee: boolean;
};

export class LecteurVoix {
  private contexte: ContexteAudio | null = null;
  private sources: AudioBufferSourceNode[] = [];
  private lecture: AbortController | null = null;
  private flux: Programmation | null = null;
  private enParole = false;

  constructor(
    private readonly apiBase: string,
    /** Appelée quand la voix commence et finit de parler (animation, état). */
    private readonly signalerParole: (parle: boolean) => void = () => {}
  ) {}

  /** La voix est-elle en train de parler ? (interruption, lot 3) */
  get parle(): boolean {
    return this.enParole;
  }

  private surParole(parle: boolean): void {
    if (this.enParole === parle) return;
    this.enParole = parle;
    this.signalerParole(parle);
  }

  /** Place des échantillons à la suite des précédents, sans trou ni chevauchement. */
  private programmer(etat: Programmation, echantillons: Float32Array, frequence: number): void {
    const contexte = this.contexte;
    if (!contexte || !echantillons.length || etat.lecture.signal.aborted) return;
    const tampon = contexte.createBuffer(1, echantillons.length, frequence);
    tampon.getChannelData(0).set(echantillons);
    const source = contexte.createBufferSource();
    source.buffer = tampon;
    source.connect(contexte.destination);
    // Retard pris (réseau lent) : on repart un peu devant l'horloge plutôt que
    // de programmer dans le passé, ce que le navigateur jouerait en accéléré.
    if (!etat.prochain || etat.prochain < contexte.currentTime) {
      etat.prochain = contexte.currentTime + AVANCE_S;
    }
    source.start(etat.prochain);
    etat.prochain += tampon.duration;
    this.sources.push(source);
    etat.derniere = source;
    this.surParole(true);
  }

  /** Signale la fin de parole quand le dernier morceau programmé a fini de jouer. */
  private finirApres(etat: Programmation): void {
    const fini = () => {
      if (this.lecture === etat.lecture) {
        this.sources = [];
        this.lecture = null;
        this.flux = null;
        this.surParole(false);
      }
    };
    const contexte = this.contexte;
    if (!etat.derniere || !contexte || contexte.currentTime >= etat.prochain) fini();
    else etat.derniere.onended = fini;
  }

  /**
   * Prépare la voix qui arrive dans le flux du chat (lot 1). Le signal rendu
   * est abandonné dès que la voix est coupée : il coupe aussi la lecture du flux.
   */
  commencerFlux(): AbortSignal {
    this.arreter();
    const lecture = new AbortController();
    this.lecture = lecture;
    this.flux = { lecture, prochain: 0, derniere: null, terminee: false };
    return lecture.signal;
  }

  /** Un morceau d'audio du flux : PCM 16 bits, 24 kHz, en base64. */
  ajouterPcm16(base64: string): void {
    const etat = this.flux;
    if (!etat || etat.terminee) return;
    try {
      this.programmer(etat, pcm16VersFlottants(base64VersOctets(base64)), FREQUENCE_VOIX);
    } catch (erreur) {
      console.warn('[GENIOUS] morceau de voix illisible', erreur);
    }
  }

  /** Plus rien n'arrivera : la parole finit avec le dernier morceau. Idempotente. */
  terminerFlux(): void {
    const etat = this.flux;
    if (!etat || etat.terminee) return;
    etat.terminee = true;
    this.finirApres(etat);
  }

  /**
   * À appeler DANS un geste du visiteur (clic) : sans cela, Safari et Chrome
   * gardent l'audio muet, et un `resume()` plus tard, hors geste, est refusé.
   */
  deverrouiller(): boolean {
    try {
      if (!this.contexte) {
        const Contexte =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Contexte) return false;
        this.contexte = new Contexte();
      }
      if (this.contexte.state === 'suspended') void this.contexte.resume();
      return true;
    } catch {
      return false;
    }
  }

  /** Coupe la voix en cours, s'il y en a une. */
  arreter(): void {
    this.lecture?.abort();
    this.lecture = null;
    this.flux = null;
    for (const source of this.sources) {
      try {
        source.stop();
      } catch {
        /* déjà arrêtée */
      }
    }
    this.sources = [];
    this.surParole(false);
  }

  /**
   * Lit une réponse déjà affichée. Silencieuse en cas d'échec : la voix
   * s'ajoute au texte, son absence ne doit jamais ressembler à une panne.
   */
  async lire(publicId: string, sessionId: string, messageId: string): Promise<void> {
    this.arreter();
    const contexte = this.contexte;
    if (!contexte || !sessionId || !messageId) return;

    const lecture = new AbortController();
    this.lecture = lecture;

    try {
      const res = await fetch(`${this.apiBase}/runtime/voix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publicId, sessionId, messageId, origin: window.location.origin }),
        signal: lecture.signal,
      });
      if (!res.ok || !res.body) return;

      // Boucle `read()` et non `for await` : un ReadableStream n'est pas
      // itérable dans Safari.
      const lecteur = res.body.getReader();
      let reste: Uint8Array = new Uint8Array(0);
      const etat: Programmation = { lecture, prochain: 0, derniere: null, terminee: false };

      for (;;) {
        const { done, value } = await lecteur.read();
        if (done || lecture.signal.aborted) break;
        const decoupe = decouperEchantillons(reste, value);
        reste = decoupe.reste;
        this.programmer(etat, decoupe.echantillons, FREQUENCE_VOIX);
      }

      if (!lecture.signal.aborted) this.finirApres(etat);
    } catch (erreur) {
      if (!lecture.signal.aborted) console.warn('[GENIOUS] lecture à voix haute impossible', erreur);
      if (this.lecture === lecture) this.surParole(false);
    }
  }
}
